// src/pages/Education/api.js
// Слой доступа к данным раздела «Образование — дошкольные организации» (АРМ, UC.13.02).
// Паттерн проекта (как в Health): getNetwork() (axios с токеном + 401-перехватчик) и API_ROOT.
//
//   GET  /api/education/meta         → { dictionaries, thresholds, permissions }   (ТЗ п.4)
//   GET  /api/education/preschools   → { rows, total }  (фильтры-разрезы в query)   (ОС шаг 2)
//        query: page, per, q, locality, type, fill_state
//   POST /api/education/preschools   → 201 + созданная запись                        (ОС шаг 7)
//        422 → { fields: { имя_поля: 'сообщение', ... } }  (АС2, серверная валидация)
//
// Отличие от Health: режим create-only, дубли НЕ проверяются (тёзки по разным
// зданиям — это разные записи, ключ — id). Поэтому 409/isDuplicate здесь нет.

import { getNetwork } from '../../utils/getNetwork';
import { API_ROOT } from '../../config';
import { META, MOCK_ROWS } from './mockData';
import { occState } from './occupancy';

// Пока бэкенда нет — true (данные в памяти). Когда появится — false.
export const USE_MOCK = true;

// Пути операций. Если бэкенд под префиксом (напр. /api/v1) — впишите его целиком,
// чтобы совпадало со Swagger. Полный URL = API_ROOT + путь.
const META_ENDPOINT = '/api/education/meta';
const LIST_ENDPOINT = '/api/education/preschools';

export class ApiError extends Error {
  constructor(status, body) {
    super('api_error');
    this.status = status;
    this.body = body;
  }
}

/* ------------------------------ Справочники ------------------------------ */
export async function fetchPreschoolMeta() {
  if (USE_MOCK) {
    return wait(200).then(() => META);
  }
  const res = await getNetwork().get(API_ROOT + META_ENDPOINT);
  return res.data;
}

/* ------------------------------- Перечень -------------------------------
 * params: { page=1, per=15, q, locality, type, fill_state }
 * Возврат: { rows, total } — total до пагинации (для счётчика «Найдено записей»).
 * В проде фильтрацию/пагинацию делает бэкенд; в моке — те же правила локально,
 * чтобы код компонента был идентичен для mock и real. */
export async function fetchPreschools(params = {}) {
  const { page = 1, per = 15, q = '', locality = '', type = '', fill_state = '' } = params;
  if (USE_MOCK) {
    return wait(300).then(() => queryMock(mockStore, { page, per, q, locality, type, fill_state }));
  }
  // Отправляем только непустые разрезы — чище query-строка.
  const query = clean({ page, per, q, locality, type, fill_state });
  const res = await getNetwork().get(API_ROOT + LIST_ENDPOINT, { params: query });
  return res.data; // ожидаем { rows, total }
}

/* ------------------------------- Создание -------------------------------
 * payload — атрибутный состав формы (Табл. 81), числа целыми/null, координаты числом. */
export async function createPreschool(payload) {
  if (USE_MOCK) {
    return mockCreate(payload);
  }
  try {
    const res = await getNetwork().post(API_ROOT + LIST_ENDPOINT, payload);
    // Перехватчик getNetwork при 4xx/5xx возвращает response (не бросает).
    if (res && res.status === 201) return res.data;
    if (res) throw new ApiError(res.status, res.data); // 422 → body.fields подсветит поля
    throw new ApiError(0, null);
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw new ApiError((e.response && e.response.status) || 0, e.response && e.response.data);
  }
}

/* --------------------------------- Мок ---------------------------------- */
let mockStore = MOCK_ROWS.slice();
let seq = Math.max(...MOCK_ROWS.map((r) => r.id)) + 1;

function mockCreate(payload) {
  return wait(350).then(() => {
    const created = {
      id: seq++,
      // Местность в атрибутном составе формы (16 полей) отсутствует — в реальном
      // потоке её выводит бэкенд (по адресу/координатам) либо форму расширяют.
      // ОТКРЫТЫЙ ВОПРОС (см. постановку бэкенда 0.2): пока в моке дефолтим, чтобы
      // строка отрисовалась. Не «чинить» на проде — согласовать источник locality.
      locality: payload.locality || 'г.Кульсары',
      locType: payload.locType || 'городская местность',
      ...payload,
    };
    mockStore = [created, ...mockStore];
    return created;
  });
}

// Фильтрация + пагинация мока по тем же правилам, что описаны для бэкенда (раздел 5.1).
function queryMock(store, { page, per, q, locality, type, fill_state }) {
  const normMin = META.thresholds.occ_norm_min;
  const needle = String(q || '').trim().toLowerCase();
  const filtered = store.filter((r) => {
    if (locality && r.locality !== locality) return false;
    if (type && r.type !== type) return false;
    if (fill_state && occState(r.seats, r.kont, normMin) !== fill_state) return false;
    if (needle) {
      const hay = `${r.name} ${r.nameKz || ''} ${r.addr} ${r.addrKz || ''}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });
  const total = filtered.length;
  const pages = Math.max(1, Math.ceil(total / per));
  const safePage = Math.min(Math.max(1, page), pages);
  const rows = filtered
    .slice((safePage - 1) * per, safePage * per)
    // Проставляем fill_state, как это сделал бы реальный API (источник истины — бэкенд).
    .map((r) => ({ ...r, fill_state: occState(r.seats, r.kont, normMin) }));
  return { rows, total };
}

function clean(obj) {
  const out = {};
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    if (v !== '' && v != null) out[k] = v;
  });
  return out;
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
